<script setup>
  import { getCurrentInstance, ref, reactive, watch, defineEmits } from 'vue'
  import JLTools from '@/util/JLTools'
  import api from '@/api'
  import { ElMessage, ElProgress } from 'element-plus'
  import { useRouter, useRoute } from 'vue-router'
  import useStore from '@/store/pinia'
  const { userStore } = useStore()
  const router = useRouter()
  const { proxy } = getCurrentInstance()

  const $emit = defineEmits(['imgsCallback'])
  const props = defineProps({
    fileMaxSize: {
      type: Number,
      required: false,
      default: 10
    },
    fileCount: {
      type: [Number, String],
      required: false,
      default: 1
    },
    fileType: {
      type: String,
      required: false,
      default: 'image/png, image/jpeg, image/jpg'
    },
    imgs: {
      type: Array
    },
    isDelete: {
      type: Boolean,
      default: true
    }
  })

  const inputRef = ref()
  const data = reactive({
    loading: false,
    imgList: [],
    previewDialog: false,
    previewUrl: '',
    selectedFiles: [],
    uploadProgress: {}, // 存储每个文件的上传进度
    uploadingFiles: [], // 当前正在上传的文件列表
    // 存储文件唯一标识与临时ID的映射，用于进度显示
    fileIdMap: new Map()
  })

  watch(
    () => props.imgs,
    (newImgs) => {
      if (!newImgs) return
      data.imgList = newImgs.filter((img) => img && img.trim() !== '')
    },
    { immediate: true }
  )

  // 生成临时ID用于上传中的文件标识
  const generateTempId = () => {
    return 'temp_' + Date.now() + '_' + Math.floor(Math.random() * 1000)
  }

  const preSelect = () => {
    inputRef.value.click()
  }

  const handleChange = (e) => {
    /* eslint-disable */
    let BreakException = {}
    let files = e.target.files
    if (!files.length) return

    if (files.length + data.imgList.length > props.fileCount) {
      ElMessage.error(`Upload a maximum of ${props.fileCount} files`)
      resetInput()
      return
    }

    try {
      for (let i = 0; i < files.length; i++) {
        if (!files[i].type || props.fileType.indexOf(files[i].type) === -1) {
          ElMessage.error(`${cleanMimeTypes(props.fileType)} format only`)
          throw BreakException
        } else if (files[i].size > props.fileMaxSize * 1024 * 1000) {
          ElMessage.error(`The file size exceeds ${props.fileMaxSize}M. Please re-upload.`)
          throw BreakException
        }
      }
    } catch (error) {
      resetInput()
      if (error !== BreakException) throw error
      return
    }

    // 检查重复文件
    const newFiles = []
    for (let i = 0; i < files.length; i++) {
      const index = data.selectedFiles.findIndex((f) => f.name === files[i].name && f.size === files[i].size && f.lastModified === files[i].lastModified)
      if (index === -1) {
        newFiles.push(files[i])
      }
    }

    if (newFiles.length === 0) {
      ElMessage.warning('No new files selected')
      resetInput()
      return
    }

    data.loading = true

    // 上传文件
    let fileSucCount = 0
    newFiles.forEach((file) => {
      // 生成临时ID
      const tempId = generateTempId()
      // 存储映射关系
      data.fileIdMap.set(tempId, file.name)
      // 添加到上传队列
      data.uploadingFiles.push({
        id: tempId,
        name: file.name,
        type: file.type
      })
      // 初始化进度
      data.uploadProgress[tempId] = 0

      const formData = new FormData()
      formData.append('file', file)

      api.common
        .uploadsFile(formData, (progressEvent) => {
          // 计算上传进度百分比
          if (progressEvent.lengthComputable) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100)
            data.uploadProgress[tempId] = percent
          }
        })
        .then((res) => {
          data.imgList.push(res)
          $emit('imgsCallback', data.imgList)
        })
        .catch((error) => {
          ElMessage.error(`File ${file.name} upload failed`)
        })
        .finally(() => {
          fileSucCount++
          data.uploadingFiles = data.uploadingFiles.filter((item) => item.id !== tempId)
          data.fileIdMap.delete(tempId)
          delete data.uploadProgress[tempId]

          if (fileSucCount === newFiles.length) {
            data.loading = false
            resetInput()
          }
        })
    })

    resetInput()
  }

  const deleteImg = (ind) => {
    data.imgList.splice(ind, 1)
    $emit('imgsCallback', data.imgList)
    resetInput()
  }

  const resetInput = () => {
    if (inputRef.value) {
      inputRef.value.value = ''
      data.selectedFiles = []
    }
  }

  const handlePictureCardPreview = (index) => {
    data.previewUrl = data.imgList[index]
    data.previewDialog = true
  }

  const cleanMimeTypes = (types) => {
    if (!types) return ''
    return types
      .split(',')
      .map((type) =>
        type
          .trim()
          .replace(/^[^/]+\//, '')
          .toUpperCase()
      )
      .join(', ')
  }

  const isVideoFile = (url) => {
    return /\.(mp4|avi|mov|wmv|mkv|m4v)$/i.test(url)
  }

  // 判断是否是图片类型
  const isImageFile = (type) => {
    return type.startsWith('image/')
  }

  // 获取文件图标
  const getFileIcon = (type) => {
    if (isImageFile(type)) {
      return 'image'
    } else if (type.startsWith('video/')) {
      return 'video'
    } else {
      return 'file'
    }
  }
</script>

<template>
  <div class="image-wrapper-box">
    <div class="img-wrapper rowSS">
      <div class="rowSC img-list-box">
        <!-- 已上传的文件 -->
        <template v-if="data.imgList.length">
          <div class="item" v-for="(item, index) in data.imgList" :key="index">
            <template v-if="isVideoFile(item)">
              <video class="dis-video" width="100px" height="100px" :controls="false">
                <source :src="JLTools.formatUrl(item)" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </template>
            <template v-else>
              <JLImg :src="item" class="dis-image" />
            </template>
            <span class="operation-box">
              <span class="item-preview icon-item" @click="handlePictureCardPreview(index)">
                <i class="icon">
                  <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill="currentColor"
                      d="m795.904 750.72 124.992 124.928a32 32 0 0 1-45.248 45.248L750.656 795.904a416 416 0 1 1 45.248-45.248zM480 832a352 352 0 1 0 0-704 352 352 0 0 0 0 704zm-32-384v-96a32 32 0 0 1 64 0v96h96a32 32 0 0 1 0 64h-96v96a32 32 0 0 1-64 0v-96h-96a32 32 0 0 1 0-64h96z"
                    ></path>
                  </svg>
                </i>
              </span>
              <span v-if="isDelete" class="item-delete icon-item" @click="deleteImg(index)">
                <i class="icon">
                  <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill="currentColor"
                      d="M160 256H96a32 32 0 0 1 0-64h256V95.936a32 32 0 0 1 32-32h256a32 32 0 0 1 32 32V192h256a32 32 0 1 1 0 64h-64v672a32 32 0 0 1-32 32H192a32 32 0 0 1-32-32V256zm448-64v-64H416v64h192zM224 896h576V256H224v640zm192-128a32 32 0 0 1-32-32V416a32 32 0 0 1 64 0v320a32 32 0 0 1-32 32zm192 0a32 32 0 0 1-32-32V416a32 32 0 0 1 64 0v320a32 32 0 0 1-32 32z"
                    ></path>
                  </svg>
                </i>
              </span>
            </span>
          </div>
        </template>

        <!-- 上传中的文件（显示环形进度） -->
        <template v-if="data.uploadingFiles.length">
          <div class="item uploading-item" v-for="file in data.uploadingFiles" :key="file.id">
            <!-- 文件名 -->
            <div class="file-name">{{ file.name }}</div>

            <!-- 环形进度条 -->
            <div class="circular-progress-container">
              <ElProgress
                :percentage="data.uploadProgress[file.id]"
                type="circle"
                :stroke-width="6"
                :width="80"
                :status="data.uploadProgress[file.id] === 100 ? 'success' : 'processing'"
              >
                <template #default>
                  <span class="progress-text">{{ data.uploadProgress[file.id] }}%</span>
                </template>
              </ElProgress>
            </div>
          </div>
        </template>

        <!-- 上传按钮 -->
        <div
          class="upload-icon"
          @click="preSelect"
          v-if="data.imgList.length + data.uploadingFiles.length < props.fileCount"
          :class="data.imgList.length + data.uploadingFiles.length > 0 ? 'upload-extend' : ''"
        >
          <span class="heng"></span>
          <span class="su"></span>
        </div>
      </div>
    </div>

    <input type="file" class="inputer" ref="inputRef" @change="handleChange($event)" multiple="multiple" />
  </div>

  <el-dialog v-model="data.previewDialog" width="800px" destroy-on-close>
    <template v-if="isVideoFile(data.previewUrl)">
      <video class="dis-video" controls style="width: 100%; height: 500px">
        <source :src="JLTools.formatUrl(data.previewUrl)" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </template>
    <template v-else>
      <JLImg :src="data.previewUrl" quality="95" class="wh100" alt="Preview Image" />
    </template>
  </el-dialog>
</template>

<style lang="less" scoped>
  .image-wrapper-box {
    width: 100%;
    padding: 10px;
    .img-list-box {
      flex-wrap: wrap;
    }
  }

  .inputer {
    width: 100%;
    height: 100%;
    min-width: 100px;
    min-height: 100px;
    display: none;
  }

  .img-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: left;
    flex-wrap: wrap;
    cursor: pointer;

    .upload-icon {
      position: relative;
      width: 100px;
      height: 100px;
      min-width: 70px;
      min-height: 70px;
      text-align: center;
      border: 1px dashed #dcdfe6;
      border-radius: 6px;
      max-width: 100px;
      max-height: 100px;
      transition: all 0.3s;

      .heng,
      .su {
        background: #c0c4cc;
        transition: all 0.3s;
      }

      .heng {
        position: absolute;
        display: inline-block;
        width: 30px;
        height: 3px;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      .su {
        position: absolute;
        display: inline-block;
        width: 3px;
        height: 30px;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      &:hover {
        background: rgba(64, 158, 255, 0.05);
        border-color: var(--Zcolor);

        .heng,
        .su {
          border-color: var(--Zcolor);
        }
      }
    }

    .upload-extend {
      margin: 0 8px 8px 0;
    }
  }

  .img-wrapper .item {
    position: relative;
    width: 100px;
    height: 100px;
    min-width: 70px;
    min-height: 70px;
    text-align: center;
    margin: 0 8px 8px 0;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    overflow: hidden;

    .dis-image,
    .dis-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 6px;
    }

    .operation-box {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      cursor: default;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      color: #fff;
      opacity: 0;
      font-size: 20px;
      background-color: rgba(0, 0, 0, 0.5);
      transition: opacity 0.3s;
      border-radius: 6px;

      .icon-item {
        margin-left: 20px;
        cursor: pointer;
        transition: transform 0.2s;

        .icon {
          height: 1em;
          width: 1em;
          color: #fff;
        }

        &:first-child {
          margin-left: 0;
        }

        &:hover {
          transform: scale(1.1);
        }
      }
    }

    &:hover .operation-box {
      opacity: 1;
    }
  }

  // 上传中项目样式
  .uploading-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #f5f7fa;

    .file-type-icon {
      position: absolute;
      color: #8c8c8c;
      z-index: 1;
    }

    .file-name {
      position: absolute;
      bottom: 5px;
      left: 0;
      right: 0;
      font-size: 12px;
      color: #606266;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 5px;
      z-index: 1;
    }

    .circular-progress-container {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      .progress-text {
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
</style>
